import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Search, Users, ShieldCheck, UserCog, AlertTriangle, X, Save, RefreshCcw, Crown, KeyRound, Copy, Sparkles, Star, PackageOpen, Ticket, Plus, Gift, UsersRound, CheckCircle2 } from "lucide-react";
import useModalA11y from "@/lib/useModalA11y";

const ROLE_META = {
  patient:   { color: "#1E9BE5", label: "Patient" },
  clinician: { color: "#22C55E", label: "Clinician" },
  admin:     { color: "#B54BE6", label: "Admin" },
};

function RoleBadge({ role }) {
  const meta = ROLE_META[role] || ROLE_META.patient;
  return (
    <span
      className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold"
      style={{
        color: meta.color,
        background: `color-mix(in oklab, ${meta.color} 14%, transparent)`,
        border: `1px solid color-mix(in oklab, ${meta.color} 30%, transparent)`,
      }}
    >
      {meta.label}
    </span>
  );
}

function Modal({ open, onClose, title, children }) {
  const ref = useModalA11y({ open, onClose });
  // Lock background scroll while the dialog is open (restored on close) so the
  // page behind can't scroll on iOS Safari and the dialog stays put.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center p-0 sm:p-4" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="fl-card-elevated w-full sm:max-w-lg flex flex-col max-h-[100dvh] sm:max-h-[90vh] rounded-none sm:rounded-2xl"
        style={{ background: "var(--fl-bg-2)" }}
      >
        <div className="flex justify-between items-center gap-3 px-5 sm:px-6 py-4 shrink-0" style={{ borderBottom: "1px solid var(--fl-border)" }}>
          <h3 className="fl-heading text-xl sm:text-2xl truncate">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="text-zinc-400 hover:text-white shrink-0"><X size={22} /></button>
        </div>
        <div
          className="overflow-y-auto px-5 sm:px-6 py-5"
          style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))", WebkitOverflowScrolling: "touch" }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function EditModal({ row, onClose, onSaved, onIssuedPw }) {
  const [name, setName] = useState(row.name || "");
  const [role, setRole] = useState(row.role);
  const [active, setActive] = useState(row.active !== false);
  const [assignedClinician, setAssignedClinician] = useState(row.assigned_clinician_id || "");
  const [clinicianOpts, setClinicianOpts] = useState([]);
  const [clinLoading, setClinLoading] = useState(row.role === "patient");
  const [tempPw, setTempPw] = useState("");
  const [busyReset, setBusyReset] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (row.role !== "patient") return;
    setClinLoading(true);
    api.get("/public/clinicians")
      .then((r) => setClinicianOpts(r.data || []))
      .catch(() => {})
      .finally(() => setClinLoading(false));
  }, [row.role]);
  const save = async () => {
    setSaving(true);
    try {
      const body = { name, role, active };
      const intended = row.role === "patient" ? (assignedClinician || null) : undefined;
      if (row.role === "patient") body.assigned_clinician_id = intended;
      const { data } = await api.patch(`/admin/users/${row.id}`, body);
      // Only confirm success once the backend echoes the intended assignment.
      if (row.role === "patient" && (data?.assigned_clinician_id || null) !== intended) {
        toast.error("Assignment did not save — please try again");
        return;
      }
      toast.success("User updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };
  const resetMfa = async () => {
    if (!window.confirm(`Reset MFA for ${row.email}?\n\nTheir authenticator will be wiped and they'll be prompted to enrol again on next login.`)) return;
    try {
      await api.post(`/admin/users/${row.id}/reset-mfa`);
      toast.success("MFA reset — they can enrol fresh at next login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };
  const resetPassword = async () => {
    if (tempPw && tempPw.length < 8) {
      toast.error("Temporary password must be at least 8 characters");
      return;
    }
    if (!window.confirm(`Force a password reset for ${row.email}?\n\nThey will be signed out (if active) and forced to choose a new password at next login.`)) return;
    setBusyReset(true);
    try {
      const { data } = await api.post(`/admin/users/${row.id}/reset-temp-password`, {
        temp_password: tempPw.trim() || null,
      });
      onIssuedPw({ email: data.email, temp_password: data.temp_password, role: data.role });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusyReset(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Edit ${row.email}`}>
      <div className="space-y-3">
        <div>
          <div className="fl-label mb-1">Name</div>
          <input className="fl-input w-full" value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-user-edit-name" />
        </div>
        <div>
          <div className="fl-label mb-1">Role</div>
          <select className="fl-input w-full" value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin-user-edit-role">
            <option value="patient">Patient</option>
            <option value="clinician">Clinician</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="admin-user-edit-active" />
          Account active (uncheck to deactivate — user cannot log in)
        </label>

        {row.role === "patient" && (
          <div>
            <div className="fl-label mb-1">Assigned clinician</div>
            {clinLoading ? (
              <div className="fl-input w-full flex items-center text-sm" style={{ color: "var(--fl-muted-2)" }}
                   data-testid="admin-user-edit-clinician-loading">
                Loading clinicians…
              </div>
            ) : (
              <select className="fl-input w-full" value={assignedClinician}
                      onChange={(e) => setAssignedClinician(e.target.value)}
                      data-testid="admin-user-edit-clinician">
                <option value="">— Unassigned —</option>
                {clinicianOpts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <div className="text-[11px] mt-1" style={{ color: "var(--fl-muted-2)" }}>
              Sets the patient's primary clinician + future booking default. Existing appointments and clinical records are not changed.
            </div>
          </div>
        )}

        <div className="pt-3 border-t space-y-4" style={{ borderColor: "var(--fl-border)" }}>
          <div>
            <div className="fl-label mb-2">Force password reset</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                className="fl-input flex-1 min-w-0"
                type="text"
                placeholder="Leave blank to auto-generate"
                value={tempPw}
                onChange={(e) => setTempPw(e.target.value)}
                data-testid="admin-user-reset-pw-input"
              />
              <button
                onClick={resetPassword}
                disabled={busyReset}
                className="fl-btn-ghost text-xs whitespace-nowrap justify-center"
                data-testid="admin-user-reset-pw-btn"
              >
                <KeyRound size={12} /> {busyReset ? "Resetting…" : "Reset password"}
              </button>
            </div>
            <div className="text-[11px] mt-1.5" style={{ color: "var(--fl-muted-2)" }}>
              Issues a temporary password and forces the user to change it at next login. Share it via a secure channel.
            </div>
          </div>

          <div>
            <div className="fl-label mb-2">MFA</div>
            <button onClick={resetMfa} className="fl-btn-ghost text-xs" data-testid="admin-user-reset-mfa">
              <ShieldCheck size={12} /> Reset MFA
            </button>
            <div className="text-[11px] mt-1.5" style={{ color: "var(--fl-muted-2)" }}>
              Wipes the user's authenticator entry. Use when a user enrolled on a different
              environment or lost access to their device.
            </div>
          </div>
        </div>

        <div className="flex gap-2 justify-end pt-3">
          <button className="fl-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="fl-btn" onClick={save} disabled={saving || clinLoading} data-testid="admin-user-edit-save"><Save size={14} /> {saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </Modal>
  );
}

function ReassignModal({ clinician, others, onClose, onDone }) {
  const [dst, setDst] = useState(others[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!dst) return toast.error("Pick a destination clinician");
    setBusy(true);
    try {
      const r = await api.post(`/admin/users/${clinician.id}/reassign`, { to_clinician_id: dst });
      toast.success(`Reassigned ${r.data.reassigned} patient(s) to ${r.data.to_clinician}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reassign failed");
    } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Reassign patients of ${clinician.name}`}>
      <p className="text-sm mb-4" style={{ color: "var(--fl-muted)" }}>
        This clinician has <b>{clinician.patient_count}</b> assigned patient(s). Pick another clinician to take over — required before deletion.
      </p>
      <select className="fl-input w-full" value={dst} onChange={(e) => setDst(e.target.value)} data-testid="admin-reassign-dst">
        {others.length === 0 ? <option value="">No other active clinicians available</option> :
          others.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.patient_count || 0} current patients</option>)}
      </select>
      <div className="flex gap-2 justify-end pt-4">
        <button className="fl-btn-ghost" onClick={onClose}>Cancel</button>
        <button className="fl-btn" onClick={run} disabled={busy || !dst} data-testid="admin-reassign-run">Reassign all</button>
      </div>
    </Modal>
  );
}

function EntitlementsModal({ row, onClose }) {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState(null);
  const [busy, setBusy] = useState("");
  const load = async () => {
    try {
      const [{ data: ent }, { data: cc }] = await Promise.all([
        api.get(`/admin/users/${row.id}/entitlements`),
        cat ? Promise.resolve({ data: cat }) : api.get(`/admin/catalogue-mini`),
      ]);
      setData(ent); setCat(cc);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const [memId, setMemId] = useState("");
  const [memDays, setMemDays] = useState(30);
  const [memNote, setMemNote] = useState("");
  const [progId, setProgId] = useState("");
  const [svcId, setSvcId] = useState("");
  const [credCount, setCredCount] = useState(1);

  const grantMembership = async () => {
    if (!memId) return toast.error("Pick a membership tier");
    setBusy("mem");
    try {
      await api.post(`/admin/users/${row.id}/entitlements/membership`,
        { package_id: memId, days: Number(memDays) || 30, note: memNote || null });
      toast.success("Membership granted");
      setMemNote(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Grant failed"); }
    finally { setBusy(""); }
  };
  const revokeMembership = async () => {
    if (!confirm("Revoke this user's membership? Access to member-only features will stop.")) return;
    setBusy("mem-revoke");
    try { await api.delete(`/admin/users/${row.id}/entitlements/membership`); toast.success("Membership revoked"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
    finally { setBusy(""); }
  };
  const grantProgramme = async () => {
    if (!progId) return toast.error("Pick a programme");
    setBusy("prog");
    try {
      const r = await api.post(`/admin/users/${row.id}/entitlements/programme`, { programme_id: progId });
      toast.success(r.data.already_granted ? "Already had this programme — no change" : "Programme granted");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Grant failed"); }
    finally { setBusy(""); }
  };
  const revokeProgramme = async (programme_id) => {
    if (!confirm("Revoke this programme? The user will lose PDF access.")) return;
    setBusy(`prog-${programme_id}`);
    try { await api.delete(`/admin/users/${row.id}/entitlements/programme/${programme_id}`); toast.success("Programme revoked"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
    finally { setBusy(""); }
  };
  const grantCredits = async () => {
    if (!svcId) return toast.error("Pick a service");
    const n = Math.max(1, Math.min(50, Number(credCount) || 1));
    setBusy("cred");
    try {
      await api.post(`/admin/users/${row.id}/entitlements/credits`, { service_id: svcId, count: n });
      toast.success(`${n} credit${n === 1 ? "" : "s"} granted`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Grant failed"); }
    finally { setBusy(""); }
  };
  const revokeCredit = async (credit_id) => {
    setBusy(`cred-${credit_id}`);
    try { await api.delete(`/admin/users/${row.id}/entitlements/credits/${credit_id}`); toast.success("Credit removed"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
    finally { setBusy(""); }
  };

  const unusedCredits = (data?.session_credits || []).filter((c) => !c.used);
  const memExpires = data?.membership?.expires_at
    ? new Date(data.membership.expires_at).toLocaleDateString("en-GB",
        { day: "numeric", month: "short", year: "numeric" })
    : null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center p-3 sm:p-4"
         style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
         onClick={onClose}>
      <div
        role="dialog" aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="fl-card-elevated w-full max-w-2xl p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
        style={{ background: "var(--fl-bg-2)" }}
        data-testid="admin-entitlements-modal"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="fl-label text-[#f47a20]">Access & entitlements</div>
            <h3 className="fl-heading text-xl sm:text-2xl mt-1">{row.name || row.email}</h3>
            <div className="text-xs mt-0.5" style={{ color: "var(--fl-muted-2)" }}>{row.email}</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-zinc-400 hover:text-white -mr-1"><X size={22} /></button>
        </div>

        {!data && <div className="text-sm py-6 text-center" style={{ color: "var(--fl-muted)" }}>Loading…</div>}

        {data && (
          <div className="space-y-5">
            {/* Membership */}
            <section className="fl-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <Star size={14} className="text-[#f47a20]" />
                <div className="fl-label text-[#f47a20]">Membership</div>
              </div>
              {data.membership ? (
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <div>
                    <div className="text-sm font-medium">{data.membership.name}</div>
                    <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
                      {data.membership.is_complimentary || data.membership.is_trial ? "Complimentary · " : ""}
                      {memExpires ? `expires ${memExpires}` : "no expiry"}
                    </div>
                  </div>
                  <button onClick={revokeMembership} className="fl-btn-ghost text-xs ml-auto"
                          disabled={busy === "mem-revoke"} data-testid="admin-ent-membership-revoke">
                    <Trash2 size={11}/> Revoke
                  </button>
                </div>
              ) : (
                <div className="text-xs mb-3" style={{ color: "var(--fl-muted-2)" }}>No active membership.</div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <select className="fl-input sm:col-span-2" value={memId} onChange={(e) => setMemId(e.target.value)}
                        data-testid="admin-ent-membership-select">
                  <option value="">Grant membership…</option>
                  {(cat?.memberships || []).map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
                <input type="number" min="1" max="3650" className="fl-input"
                       value={memDays} onChange={(e) => setMemDays(e.target.value)}
                       placeholder="Days" data-testid="admin-ent-membership-days"/>
                <button onClick={grantMembership} disabled={!memId || busy === "mem"}
                        className="fl-btn text-xs justify-center" data-testid="admin-ent-membership-grant">
                  <Gift size={12}/> Grant
                </button>
              </div>
              <input className="fl-input mt-2 w-full text-xs"
                     placeholder="Reason / note (optional)"
                     value={memNote} onChange={(e) => setMemNote(e.target.value)}
                     data-testid="admin-ent-membership-note"/>
            </section>

            {/* Programme grants */}
            <section className="fl-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <PackageOpen size={14} className="text-[#f47a20]" />
                <div className="fl-label text-[#f47a20]">Online programmes ({data.programme_grants.length})</div>
              </div>
              {data.programme_grants.length > 0 && (
                <ul className="space-y-2 mb-3">
                  {data.programme_grants.map((g) => (
                    <li key={g.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0 last:pb-0"
                        data-testid={`admin-ent-programme-row-${g.programme_id}`}>
                      <div className="min-w-0">
                        <div className="truncate">{g.programme_name || g.programme_id}</div>
                        <div className="text-[10px]" style={{ color: "var(--fl-muted-2)" }}>
                          {g.is_complimentary || g.trial ? "Complimentary · " : ""}
                          {new Date(g.granted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                        </div>
                      </div>
                      <button onClick={() => revokeProgramme(g.programme_id)}
                              disabled={busy === `prog-${g.programme_id}`}
                              className="fl-btn-ghost text-xs text-red-400"
                              data-testid={`admin-ent-programme-revoke-${g.programme_id}`}>
                        <Trash2 size={11}/> Revoke
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <select className="fl-input flex-1" value={progId} onChange={(e) => setProgId(e.target.value)}
                        data-testid="admin-ent-programme-select">
                  <option value="">Grant programme…</option>
                  {(cat?.programmes || []).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <button onClick={grantProgramme} disabled={!progId || busy === "prog"}
                        className="fl-btn text-xs whitespace-nowrap justify-center"
                        data-testid="admin-ent-programme-grant">
                  <Plus size={12}/> Grant
                </button>
              </div>
            </section>

            {/* Session credits */}
            <section className="fl-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <Ticket size={14} className="text-[#f47a20]" />
                <div className="fl-label text-[#f47a20]">Session credits ({unusedCredits.length} unused)</div>
              </div>
              {unusedCredits.length > 0 ? (
                <ul className="space-y-2 mb-3 max-h-40 overflow-y-auto pr-1">
                  {unusedCredits.slice(0, 30).map((c) => (
                    <li key={c.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0 last:pb-0"
                        data-testid={`admin-ent-credit-row-${c.id}`}>
                      <div className="min-w-0">
                        <div className="truncate">{c.service_name}</div>
                        <div className="text-[10px]" style={{ color: "var(--fl-muted-2)" }}>
                          {c.is_complimentary || c.trial ? "Complimentary · " : ""}
                          granted {new Date(c.granted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                        </div>
                      </div>
                      <button onClick={() => revokeCredit(c.id)}
                              disabled={busy === `cred-${c.id}`}
                              className="fl-btn-ghost text-xs text-red-400"
                              data-testid={`admin-ent-credit-revoke-${c.id}`}>
                        <Trash2 size={11}/>
                      </button>
                    </li>
                  ))}
                  {unusedCredits.length > 30 && (
                    <li className="text-[10px]" style={{ color: "var(--fl-muted-2)" }}>
                      + {unusedCredits.length - 30} more not shown
                    </li>
                  )}
                </ul>
              ) : (
                <div className="text-xs mb-3" style={{ color: "var(--fl-muted-2)" }}>No unused credits.</div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
                <select className="fl-input sm:col-span-4" value={svcId} onChange={(e) => setSvcId(e.target.value)}
                        data-testid="admin-ent-credits-service">
                  <option value="">Pick a service…</option>
                  {(cat?.services || []).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <input type="number" min="1" max="50" className="fl-input"
                       value={credCount} onChange={(e) => setCredCount(e.target.value)}
                       placeholder="Qty" data-testid="admin-ent-credits-count"/>
                <button onClick={grantCredits} disabled={!svcId || busy === "cred"}
                        className="fl-btn text-xs justify-center" data-testid="admin-ent-credits-grant">
                  <Plus size={12}/> Grant
                </button>
              </div>
            </section>
          </div>
        )}

        <div className="flex justify-end mt-5">
          <button onClick={onClose} className="fl-btn-ghost text-sm" data-testid="admin-ent-close">Done</button>
        </div>
      </div>
    </div>
  );
}

function BulkGrantModal({ onClose, presetEmails }) {
  const [cat, setCat] = useState(null);
  const [emailsText, setEmailsText] = useState(presetEmails || "");
  const [kind, setKind] = useState("membership");
  const [memId, setMemId] = useState("");
  const [memDays, setMemDays] = useState(30);
  const [progId, setProgId] = useState("");
  const [svcId, setSvcId] = useState("");
  const [credCount, setCredCount] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/admin/catalogue-mini").then((r) => alive && setCat(r.data))
       .catch((e) => toast.error(e?.response?.data?.detail || "Catalogue load failed"));
    return () => { alive = false; };
  }, []);

  const parseEmails = () => (emailsText || "")
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e && e.includes("@"));

  const emails = parseEmails();

  const run = async () => {
    if (!emails.length) return toast.error("Add at least one valid email address.");
    if (emails.length > 500) return toast.error("Max 500 users per bulk call.");
    const body = { emails, kind };
    if (kind === "membership") {
      if (!memId) return toast.error("Pick a membership tier.");
      body.membership = { package_id: memId, days: Number(memDays) || 30, note: note || null };
    } else if (kind === "programme") {
      if (!progId) return toast.error("Pick a programme.");
      body.programme = { programme_id: progId, note: note || null };
    } else if (kind === "credits") {
      if (!svcId) return toast.error("Pick a service.");
      body.credits = { service_id: svcId, count: Math.max(1, Math.min(50, Number(credCount) || 1)), note: note || null };
    }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/entitlements/bulk", body);
      setResult(data);
      const t = data.totals;
      toast.success(`Done — ${t.granted} granted · ${t.already} already had · ${t.not_found} not found${t.error ? " · " + t.error + " errors" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk grant failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center p-3 sm:p-4"
         style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
         onClick={onClose}>
      <div
        role="dialog" aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="fl-card-elevated w-full max-w-2xl p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
        style={{ background: "var(--fl-bg-2)" }}
        data-testid="admin-bulk-grant-modal"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="fl-label text-[#f47a20]">Bulk grant</div>
            <h3 className="fl-heading text-xl sm:text-2xl mt-1">Grant access to a list of users</h3>
            <p className="text-xs mt-1 max-w-lg" style={{ color: "var(--fl-muted-2)" }}>
              Paste emails (comma, space or newline separated). One entitlement is applied to each. Existing paid access is preserved for programmes and credits; membership will be replaced.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-zinc-400 hover:text-white -mr-1"><X size={22} /></button>
        </div>

        <div className="space-y-4">
          {/* Emails */}
          <div>
            <div className="fl-label mb-1">Emails ({emails.length} parsed)</div>
            <textarea
              className="fl-input w-full text-sm font-mono min-h-[120px]"
              placeholder="alice@example.com&#10;bob@example.com, carol@example.com"
              value={emailsText}
              onChange={(e) => setEmailsText(e.target.value)}
              data-testid="admin-bulk-emails"
            />
          </div>

          {/* Kind selector */}
          <div>
            <div className="fl-label mb-1">Entitlement</div>
            <div className="flex gap-2 flex-wrap">
              {["membership", "programme", "credits"].map((k) => (
                <button key={k}
                        onClick={() => setKind(k)}
                        data-testid={`admin-bulk-kind-${k}`}
                        className={"text-xs px-3 py-1.5 rounded-md border transition-colors " +
                                   (kind === k
                                    ? "border-[#f47a20] text-[#f47a20] bg-[#f47a20]/10"
                                    : "border-white/10 text-zinc-300 hover:border-white/25")}>
                  {k === "membership" ? "Membership" : k === "programme" ? "Online programme" : "Session credits"}
                </button>
              ))}
            </div>
          </div>

          {/* Params per kind */}
          {kind === "membership" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <select className="fl-input sm:col-span-2" value={memId} onChange={(e) => setMemId(e.target.value)}
                      data-testid="admin-bulk-membership-select">
                <option value="">Pick membership…</option>
                {(cat?.memberships || []).map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
              <input type="number" min="1" max="3650" className="fl-input"
                     value={memDays} onChange={(e) => setMemDays(e.target.value)}
                     placeholder="Days" data-testid="admin-bulk-membership-days"/>
            </div>
          )}

          {kind === "programme" && (
            <select className="fl-input w-full" value={progId} onChange={(e) => setProgId(e.target.value)}
                    data-testid="admin-bulk-programme-select">
              <option value="">Pick programme…</option>
              {(cat?.programmes || []).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}

          {kind === "credits" && (
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
              <select className="fl-input sm:col-span-4" value={svcId} onChange={(e) => setSvcId(e.target.value)}
                      data-testid="admin-bulk-credits-service">
                <option value="">Pick service…</option>
                {(cat?.services || []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <input type="number" min="1" max="50" className="fl-input"
                     value={credCount} onChange={(e) => setCredCount(e.target.value)}
                     placeholder="Qty per user" data-testid="admin-bulk-credits-count"/>
            </div>
          )}

          <input className="fl-input w-full text-sm"
                 placeholder="Reason / note (optional, applied to all)"
                 value={note} onChange={(e) => setNote(e.target.value)}
                 data-testid="admin-bulk-note"/>

          <button onClick={run} disabled={busy || !emails.length}
                  className="fl-btn w-full justify-center text-sm"
                  data-testid="admin-bulk-apply">
            <Gift size={13}/> {busy ? "Applying…" : `Grant to ${emails.length} user${emails.length === 1 ? "" : "s"}`}
          </button>

          {/* Results */}
          {result && (
            <div className="fl-card p-4 space-y-2" data-testid="admin-bulk-result">
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 size={12}/> {result.totals.granted} granted</span>
                <span className="text-zinc-400">{result.totals.already} already had</span>
                <span className="text-amber-400">{result.totals.not_found} not found</span>
                {result.totals.error ? <span className="text-red-400">{result.totals.error} errors</span> : null}
              </div>
              <ul className="max-h-56 overflow-y-auto pr-1 text-xs space-y-1">
                {result.results.map((r) => (
                  <li key={r.email} className="flex items-center justify-between border-b border-white/5 pb-1 last:border-0"
                      data-testid={`admin-bulk-result-row-${r.status}`}>
                    <span className="truncate mr-2">{r.email}</span>
                    <span className={
                      r.status === "granted" ? "text-emerald-400" :
                      r.status === "already" ? "text-zinc-400" :
                      r.status === "not_found" ? "text-amber-400" : "text-red-400"}>
                      {r.status.replace("_", " ")}
                      {r.detail ? ` · ${r.detail}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-5">
          <button onClick={onClose} className="fl-btn-ghost text-sm" data-testid="admin-bulk-close">Close</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [editing, setEditing] = useState(null);
  const [reassigning, setReassigning] = useState(null);
  const [issuedPw, setIssuedPw] = useState(null);
  const [entitling, setEntitling] = useState(null);
  const [bulk, setBulk] = useState(null);   // { presetEmails? }
  // Reuse the same clinician source as the Edit User modal to resolve
  // assigned_clinician_id -> display name in the patient rows.
  const [clinicianMap, setClinicianMap] = useState({});
  useEffect(() => {
    api.get("/public/clinicians").then((r) => {
      const m = {};
      (r.data || []).forEach((c) => { m[c.id] = c.name; });
      setClinicianMap(m);
    }).catch(() => {});
  }, []);
  const copyPw = async () => {
    if (!issuedPw?.temp_password) return;
    try { await navigator.clipboard.writeText(issuedPw.temp_password); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  const refresh = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("q", search);
    if (role) params.set("role", role);
    api.get(`/admin/users?${params}`).then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const clinicians = useMemo(() => rows.filter((r) => r.role === "clinician"), [rows]);

  const doDelete = async (row) => {
    if (row.is_owner) return toast.error("Owner account can only delete itself");
    if (row.role === "clinician" && (row.patient_count || 0) > 0) {
      setReassigning(row);
      return;
    }
    if (!confirm(`Permanently delete ${row.email}? This cannot be undone.`)) return;
    try {
      const r = await api.delete(`/admin/users/${row.id}`);
      toast.success(`Deleted (${Object.values(r.data.deleted || {}).reduce((a, b) => a + b, 0)} related records cleaned)`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const bulkDeletePatients = async () => {
    const n = rows.filter((r) => r.role === "patient").length;
    if (n === 0) return toast.info("No patient accounts to delete.");
    if (!confirm(`Permanently delete ALL ${n} patient account(s)? This is a one-shot clean-slate action.`)) return;
    try {
      const r = await api.post("/admin/users/bulk-delete-patients");
      toast.success(`${r.data.deleted_patients} patient(s) permanently deleted`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    }
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-start flex-wrap gap-4 mb-8">
        <div>
          <div className="fl-label text-[#f47a20]">Directory</div>
          <h1 className="fl-heading text-4xl mt-2 flex items-center gap-3"><UserCog size={28} /> User management</h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--fl-muted)" }}>
            Search, edit, deactivate or permanently delete accounts. Deleting a clinician with active patients requires you to reassign them first — this keeps care from being dropped mid-treatment.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setBulk({ presetEmails: "" })}
            className="fl-btn text-sm"
            data-testid="admin-users-bulk-grant"
          >
            <UsersRound size={13} /> Bulk grant
          </button>
          <button
            onClick={bulkDeletePatients}
            className="fl-btn-ghost text-sm border-red-700 text-red-500 hover:bg-red-950/40"
            data-testid="admin-users-bulk-delete-patients"
          >
            <Trash2 size={13} /> Delete ALL patient accounts
          </button>
        </div>
      </div>

      <div className="fl-card p-4 mb-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            placeholder="Search email or name"
            className="fl-input w-full pl-9 text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && refresh()}
            data-testid="admin-users-search"
          />
        </div>
        <select className="fl-input text-sm" value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin-users-role-filter">
          <option value="">All roles</option>
          <option value="patient">Patients</option>
          <option value="clinician">Clinicians</option>
          <option value="admin">Admins</option>
        </select>
        <button onClick={refresh} className="fl-btn-ghost text-sm" data-testid="admin-users-refresh"><RefreshCcw size={13} /> Refresh</button>
      </div>

      {loading ? (
        <div className="fl-card p-6 text-sm" style={{ color: "var(--fl-muted)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="fl-card p-10 text-center text-sm" style={{ color: "var(--fl-muted)" }}>No users match this filter.</div>
      ) : (
        <div className="fl-card overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-3 p-3 border-b border-white/10 fl-label">
            <div className="col-span-4">User</div>
            <div className="col-span-2">Role</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Info</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {rows.map((r) => (
            <div
              key={r.id}
              className="md:grid md:grid-cols-12 md:gap-3 flex flex-col gap-2 p-3 border-b border-white/5 md:items-center text-sm hover:bg-white/5"
              data-testid={`admin-users-row-${r.id}`}
            >
              <div className="md:col-span-4 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="truncate font-medium">{r.name || "—"}</span>
                  {r.is_owner && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wider" style={{ color: "#F5B700", background: "color-mix(in oklab, #F5B700 14%, transparent)", border: "1px solid color-mix(in oklab, #F5B700 30%, transparent)" }} title="Owner account — can only be modified by itself">
                      <Crown size={9} /> Owner
                    </span>
                  )}
                </div>
                <div className="text-[11px] truncate" style={{ color: "var(--fl-muted-2)" }}>{r.email}</div>
              </div>
              <div className="md:col-span-2 flex items-center gap-2 md:block">
                <span className="fl-label md:hidden">Role</span>
                <RoleBadge role={r.role} />
              </div>
              <div className="md:col-span-2 flex items-center gap-2 md:block">
                <span className="fl-label md:hidden">Status</span>
                {r.active === false
                  ? <span className="text-[11px] text-red-400">Deactivated</span>
                  : <span className="text-[11px] text-green-400">Active</span>}
              </div>
              <div className="md:col-span-2 text-[11px] min-w-0" style={{ color: "var(--fl-muted)" }}>
                {r.role === "clinician" && <>{r.patient_count || 0} patient{r.patient_count === 1 ? "" : "s"}</>}
                {r.role === "patient" && (
                  <div className="flex flex-col gap-0.5 min-w-0">
                    {r.contact_status === "new" && <span className="text-red-400">NEW — to contact</span>}
                    <span className="truncate" data-testid={`admin-users-assigned-${r.id}`}>
                      {r.assigned_clinician_id
                        ? (clinicianMap[r.assigned_clinician_id] || "Assigned")
                        : "Unassigned"}
                    </span>
                  </div>
                )}
              </div>
              <div className="md:col-span-2 flex gap-2 md:justify-end pt-1 md:pt-0 flex-wrap">
                <button
                  className="fl-btn-ghost text-xs px-2 py-1"
                  onClick={() => setEntitling(r)}
                  title="Grant or revoke membership / programmes / credits"
                  data-testid={`admin-users-access-${r.id}`}
                >
                  <Sparkles size={11}/> Access
                </button>
                <button
                  className="fl-btn-ghost text-xs px-2 py-1"
                  onClick={() => setEditing(r)}
                  disabled={r.is_owner}
                  title={r.is_owner ? "Owner account can only edit itself" : "Edit"}
                  data-testid={`admin-users-edit-${r.id}`}
                >
                  Edit
                </button>
                <button
                  className="fl-btn-ghost text-xs px-2 py-1 text-red-400"
                  onClick={() => doDelete(r)}
                  disabled={r.is_owner}
                  title={r.is_owner ? "Owner account cannot be deleted here" : "Delete"}
                  data-testid={`admin-users-delete-${r.id}`}
                >
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
          onIssuedPw={(p) => { setIssuedPw(p); refresh(); }}
        />
      )}
      {entitling && (
        <EntitlementsModal
          row={entitling}
          onClose={() => setEntitling(null)}
        />
      )}
      {bulk && (
        <BulkGrantModal
          presetEmails={bulk.presetEmails || ""}
          onClose={() => setBulk(null)}
        />
      )}
      {reassigning && (
        <ReassignModal
          clinician={reassigning}
          others={clinicians.filter((c) => c.id !== reassigning.id && c.active !== false)}
          onClose={() => setReassigning(null)}
          onDone={() => { setReassigning(null); refresh(); }}
        />
      )}
      {issuedPw && (
        <Modal open onClose={() => setIssuedPw(null)} title="Temporary password issued">
          <div className="space-y-4">
            <p className="text-sm" style={{ color: "var(--fl-muted)" }}>
              Copy this password now — it will be shown only once. Share it with <b>{issuedPw.email}</b> via a secure channel. They will be forced to choose a new password at next login.
            </p>
            <div className="fl-card p-4 flex items-center justify-between gap-4" style={{ background: "var(--fl-bg-2)" }}>
              <code className="text-sm text-white break-all" data-testid="admin-user-reset-issued-value">{issuedPw.temp_password}</code>
              <button onClick={copyPw} className="fl-btn text-xs whitespace-nowrap" data-testid="admin-user-reset-copy">
                <Copy size={12} /> Copy
              </button>
            </div>
            <div className="flex justify-end">
              <button onClick={() => setIssuedPw(null)} className="fl-btn" data-testid="admin-user-reset-done">Done</button>
            </div>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}
